import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Boxes,
  Check,
  Copy,
  Download,
  ExternalLink,
  HardDrive,
  Loader2,
  Monitor,
  Terminal,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

type BitswanBuild = { version: string; sha256: string; size: number; fetched_at: string };
type AocBuild = { version: string; checksum: string; is_current: boolean; published_at: string };
type RegistryImage = {
  name: string;
  group: 'aoc' | 'bailey' | 'shared';
  tags: string[];
  builds: string[];
  latest_digest: string;
  tag_count: number;
};
type RegistryUsage = {
  total_bytes: number;
  blob_count: number;
  by_repository: Record<string, number>;
  disk_total_bytes: number;
  disk_used_bytes: number;
  disk_free_bytes: number;
};
type PruneResult = {
  keep: number;
  applied: boolean;
  removed: { repository: string; tag: string; digest: string; deleted: boolean }[];
  note: string;
};

type Software = {
  bitswan_cli: BitswanBuild[];
  aoc_cli: AocBuild[];
  registry: {
    host: string;
    images: RegistryImage[];
    error: string | null;
    usage: RegistryUsage | null;
  };
  backend_url: string;
};

const when = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const megabytes = (bytes: number) => `${(bytes / 1_048_576).toFixed(1)} MB`;

const gigabytes = (bytes: number) =>
  bytes >= 1_073_741_824 ? `${(bytes / 1_073_741_824).toFixed(2)} GB` : megabytes(bytes);

const GROUP_TITLE = {
  aoc: 'AOC images',
  bailey: 'Bailey images',
  shared: 'Third-party images',
} as const;

const GROUP_BLURB = {
  aoc: 'What this AOC runs, and what an instance downstream of it copies.',
  bailey: 'What a Bailey pulls from here.',
  shared: 'Vendored copies of images we did not build, so nobody has to reach Docker Hub.',
} as const;

function Copyable({ text, label }: { text: string; label: string }) {
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-md bg-muted p-3 pr-12 text-xs leading-relaxed">
        <code>{text}</code>
      </pre>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-1 top-1 h-7 w-7"
        aria-label={`Copy ${label}`}
        onClick={() => {
          navigator.clipboard.writeText(text);
          toast.success('Copied');
        }}
      >
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}



type ReleaseImage = { repository: string; digest: string; reference: string };
type Release = {
  version: string;
  published_at: string;
  origin: string;
  git_sha: string;
  is_current: boolean;
  complete?: boolean;
  missing?: string[];
  images: Record<string, ReleaseImage>;
  artifacts?: { kind: string; name: string; digest: string; reference: string }[];
  changed?: string[];
};

function Releases({ product }: { product: 'aoc' | 'bailey' }) {
  const [open, setOpen] = useState<string | null>(null);
  const releases = useQuery({
    queryKey: ['releases', product],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get(`/global-admin/releases/?product=${product}`)).data as {
        current: Release | null;
        releases: Release[];
      };
    },
  });

  const rows = releases.data?.releases ?? [];

  return (
    <Card data-testid={`${product}-releases-card`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Boxes className="h-4 w-4" />
          Releases
        </CardTitle>
        <CardDescription>
          One release is one set of images and binaries, cut by the builds of a commit.
          It is complete only once every one of them has reported, and runs whole or not
          at all.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {releases.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid={`no-${product}-releases`}>
            Nothing released yet. CI records a release as each build of a commit reports in.
          </p>
        ) : (
          <Table data-testid={`${product}-release-list`}>
            <TableHeader>
              <TableRow>
                <TableHead>Version</TableHead>
                <TableHead>Published</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Contents</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((release) => (
                <>
                  <TableRow key={release.version} data-testid={`release-${release.version}`}>
                    <TableCell className="font-mono text-sm">{release.version}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {when(release.published_at)}
                    </TableCell>
                    <TableCell>
                      {release.is_current ? (
                        <Badge>Running here</Badge>
                      ) : release.complete === false ? (
                        <Badge variant="outline">
                          Waiting on {(release.missing ?? []).length}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Available</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setOpen(open === release.version ? null : release.version)}
                        data-testid={`release-contents-${release.version}`}
                      >
                        {(release.artifacts ?? []).length} items
                      </Button>
                    </TableCell>
                  </TableRow>
                  {open === release.version && (
                    <TableRow key={`${release.version}-contents`}>
                      <TableCell colSpan={4} className="bg-muted/30">
                        <div className="space-y-1 py-1">
                          {(release.artifacts ?? []).map((a) => (
                            <div
                              key={a.name}
                              className="flex flex-wrap items-baseline gap-2 text-xs"
                              data-testid={`artifact-${a.name}`}
                            >
                              <Badge variant="outline" className="font-normal">
                                {a.kind}
                              </Badge>
                              <code className="font-mono">{a.name}</code>
                              <code className="text-muted-foreground">
                                {a.digest.slice(0, 19)}…
                              </code>
                            </div>
                          ))}
                          {(release.missing ?? []).length > 0 && (
                            <p className="pt-1 text-xs text-amber-700">
                              Still waiting on {(release.missing ?? []).join(', ')}.
                            </p>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

type DemoRelease = {
  version: string;
  channel: string;
  pull_request: number | null;
  is_current: boolean;
  complete: boolean;
  published_at: string;
};
type Demo = {
  version: string;
  url: string;
  content_url: string;
  git_sha: string;
  uploaded_at: string;
  size: number;
  sha256: string;
  file_count: number;
  release: DemoRelease | null;
};
function releaseLabel(release: DemoRelease | null) {
  if (release === null) return 'no release of this version is recorded here';
  if (release.channel === 'pull-request') {
    return release.pull_request === null
      ? 'on the pull-request channel'
      : `pull request #${release.pull_request}, on the pull-request channel`;
  }
  return release.is_current ? 'public, and running here' : 'public';
}

function Demos() {
  const [params, setParams] = useSearchParams();
  const asked = params.get('demo');
  const card = useRef<HTMLDivElement>(null);

  const demos = useQuery({
    queryKey: ['bailey-demos'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/demos/')).data as { demos: Demo[] };
    },
  });

  const rows = demos.data?.demos ?? [];
  const shown = rows.find((demo) => demo.version === asked) ?? null;
  const listed = !demos.isLoading;

  useEffect(() => {
    if (asked === null || !listed) return;
    card.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [asked, listed]);

  const show = (version: string | null) => {
    const next = new URLSearchParams(params);
    if (version === null) next.delete('demo');
    else next.set('demo', version);
    setParams(next, { replace: true });
  };

  return (
    <Card ref={card} data-testid="bailey-demos-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Monitor className="h-4 w-4" />
          Demos
        </CardTitle>
        <CardDescription>
          Every release is built with a demo of the two Bailey screens — the workspace
          dashboard and the server console — as a bundle that runs entirely in the
          browser against fixed sample data. Opening one here contacts no server and
          changes nothing. Only the most recent are kept.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {demos.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="no-bailey-demos">
            No demo has been published here yet. Bailey's CI uploads one with a push key
            as part of releasing what it tested.
          </p>
        ) : (
          <>
            {asked !== null && shown === null && (
              <p className="text-sm text-amber-700" data-testid="demo-not-held">
                This AOC no longer holds the demo of {asked}. The ones it still has are
                listed below.
              </p>
            )}
            <Table data-testid="bailey-demo-list">
              <TableHeader>
                <TableRow>
                  <TableHead>Release</TableHead>
                  <TableHead>Where that release stands</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead className="text-right">Size</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((demo) => (
                  <TableRow key={demo.version} data-testid={`bailey-demo-${demo.version}`}>
                    <TableCell className="font-mono text-sm">{demo.version}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {releaseLabel(demo.release)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {when(demo.uploaded_at)}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {megabytes(demo.size)}
                    </TableCell>
                    <TableCell className="space-x-1 text-right">
                      <Button
                        variant={shown?.version === demo.version ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() =>
                          show(shown?.version === demo.version ? null : demo.version)
                        }
                        data-testid={`show-demo-${demo.version}`}
                      >
                        {shown?.version === demo.version ? 'Hide' : 'Show'}
                      </Button>
                      <Button variant="ghost" size="sm" asChild>
                        <a
                          href={demo.url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`open-demo-${demo.version}`}
                        >
                          <ExternalLink className="mr-1 h-3.5 w-3.5" />
                          New tab
                        </a>
                      </Button>
                      <Button variant="ghost" size="sm" asChild>
                        <a
                          href={`${demo.content_url}bailey-demo.tar.gz`}
                          download
                          data-testid={`download-demo-${demo.version}`}
                        >
                          <Download className="mr-1 h-3.5 w-3.5" />
                          Bundle
                        </a>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {shown !== null && (
              <div className="space-y-2" data-testid={`demo-preview-${shown.version}`}>
                <p className="text-sm text-muted-foreground">
                  The demo of {shown.version}, {shown.file_count} files built from commit{' '}
                  <code className="rounded bg-muted px-1 font-mono text-xs">
                    {shown.git_sha.slice(0, 8)}
                  </code>
                  . Only global admins and support agents can open a demo.
                </p>
                <iframe
                  key={shown.version}
                  src={shown.content_url}
                  sandbox="allow-scripts allow-forms allow-popups allow-modals allow-downloads"
                  title={`Bailey demo ${shown.version}`}
                  className="h-[70vh] min-h-[480px] w-full rounded-md border bg-white"
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RegistryImages({
  group,
  software,
  registryHost,
}: {
  group: 'aoc' | 'bailey' | 'shared';
  software: { data?: Software };
  registryHost: string;
}) {
  const inGroup = (software.data?.registry.images ?? []).filter((i) => i.group === group);
  return (
    <Card data-testid={`registry-${group}-card`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Boxes className="h-4 w-4" />
          {GROUP_TITLE[group]}
        </CardTitle>
        <CardDescription>
          {GROUP_BLURB[group]}{' '}
          {registryHost ? `Served from ${registryHost}.` : 'No registry host is configured.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {software.data?.registry.error ? (
          <div
            className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="registry-unreachable"
          >
            <p>The registry could not be listed: {software.data.registry.error}</p>
            <p className="mt-1">
              An AOC installed before the registry existed has no signing key for it;
              running <code className="rounded bg-amber-100 px-1">update</code> gives it one.
            </p>
          </div>
        ) : inGroup.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid={`registry-${group}-empty`}>
            Nothing here yet.
          </p>
        ) : (
          <Table data-testid={`registry-${group}-list`}>
            <TableHeader>
              <TableRow>
                <TableHead>Image</TableHead>
                <TableHead>Current</TableHead>
                <TableHead>Previous builds</TableHead>
                <TableHead className="text-right">Size</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inGroup.map((image) => {
                const [current, ...older] = image.builds;
                const bytes = software.data?.registry.usage?.by_repository?.[image.name];
                return (
                  <TableRow key={image.name} data-testid={`registry-image-${image.name}`}>
                    <TableCell className="font-mono text-sm">{image.name}</TableCell>
                    <TableCell>
                      {current ? (
                        <code
                          className="rounded bg-muted px-1.5 py-0.5 text-xs"
                          data-testid={`registry-current-${image.name}`}
                        >
                          {current}
                        </code>
                      ) : (
                        <span className="text-xs text-muted-foreground">no build tag</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {older.length === 0 ? (
                        <span className="text-xs text-muted-foreground">none</span>
                      ) : (
                        <details>
                          <summary className="cursor-pointer text-xs text-muted-foreground">
                            {older.length} older
                          </summary>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {older.map((tag) => (
                              <code key={tag} className="rounded bg-muted px-1.5 py-0.5 text-xs">
                                {tag}
                              </code>
                            ))}
                          </div>
                        </details>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {bytes === undefined ? '—' : megabytes(bytes)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default function SoftwarePage() {
  const [keep, setKeep] = useState('5');
  const [planned, setPlanned] = useState<PruneResult | null>(null);

  const software = useQuery({
    queryKey: ['software'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/software/')).data as Software;
    },
  });

  const prune = useMutation({
    mutationFn: async (apply: boolean) => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.post('/global-admin/registry/prune', {
        keep: Number(keep), apply,
      })).data as PruneResult;
    },
    onSuccess: (result) => {
      setPlanned(result);
      if (result.applied) {
        toast.success(`Removed ${result.removed.filter((r) => r.deleted).length} builds.`);
        software.refetch();
      }
    },
    onError: () => toast.error('The registry refused that.'),
  });

  const api = software.data?.backend_url ?? '';
  const registryHost = software.data?.registry.host ?? '';
  const currentAoc = software.data?.aoc_cli.find((row) => row.is_current);
  const newestBitswan = software.data?.bitswan_cli[0];

  if (software.isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="software-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Downloads</h2>
        <p className="text-muted-foreground">
          What this AOC serves: the two command-line tools, and the images in its registry.
          Both CLIs download freely — a licence key is what lets one of them reach the
          registry, so a customer whose licence has lapsed keeps a working CLI but stops
          being able to pull.
        </p>
      </div>

      <section className="space-y-4" data-testid="aoc-section">
        <div className="border-b pb-2">
          <h3 className="text-xl font-semibold tracking-tight">AOC</h3>
          <p className="text-sm text-muted-foreground">
            The operations centre itself: the images it runs, the releases they were cut
            into, and the tool that installs and updates it.
          </p>
        </div>
      <Card data-testid="aoc-cli-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Terminal className="h-4 w-4" />
            On-prem AOC CLI
            {currentAoc && <Badge variant="secondary">{currentAoc.version}</Badge>}
          </CardTitle>
          <CardDescription>
            What a customer installs an AOC on their own machine with. CI publishes a build
            here with a push key; anyone may download it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {software.data?.aoc_cli.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing published yet. CI pushes a build to
              <code className="mx-1 rounded bg-muted px-1">/api/frontend/on-prem-aoc/publish</code>
              with a push key.
            </p>
          ) : (
            <Table data-testid="aoc-cli-list">
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead>SHA-256</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {software.data?.aoc_cli.map((row) => (
                  <TableRow key={row.version} data-testid={`aoc-cli-${row.version}`}>
                    <TableCell className="font-medium">{row.version}</TableCell>
                    <TableCell className="text-muted-foreground">{when(row.published_at)}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.checksum.slice(0, 12)}…
                    </TableCell>
                    <TableCell className="text-right">
                      {row.is_current && (
                        <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">
                          <Check className="mr-1 h-3 w-3" />
                          current
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="space-y-2">
            <p className="text-sm font-medium">How a customer downloads it</p>
            <Copyable
              label="the on-prem AOC CLI download"
              text={`curl -fsSL ${api}/api/frontend/on-prem-aoc/binary \\
  -o bitswan-on-prem-aoc
chmod +x bitswan-on-prem-aoc
sudo mv bitswan-on-prem-aoc /usr/local/bin/`}
            />
            <p className="text-xs text-muted-foreground">
              No key needed — they need one to pull images, not to hold the program, and
              they have nowhere to get one before they can run it. Once installed,
              <code className="mx-1 rounded bg-muted px-1">bitswan-on-prem-aoc upgrade</code>
              fetches later builds from here, so this is only needed once.
            </p>
          </div>
        </CardContent>
      </Card>
        <Releases product="aoc" />
        <RegistryImages group="aoc" software={software} registryHost={registryHost} />
      </section>

      <section className="space-y-4" data-testid="bailey-section">
        <div className="border-b pb-2">
          <h3 className="text-xl font-semibold tracking-tight">Bailey</h3>
          <p className="text-sm text-muted-foreground">
            What a Bailey pulls from this AOC, and the tool that installs one.
          </p>
        </div>
      <Card data-testid="bitswan-cli-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Terminal className="h-4 w-4" />
            bitswan CLI
            {newestBitswan && <Badge variant="secondary">{newestBitswan.version}</Badge>}
          </CardTitle>
          <CardDescription>
            Published to this AOC and kept in object storage. A Bailey pulls it from
            here; nothing is fetched from anywhere else.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {software.data?.bitswan_cli.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing mirrored yet. The first server to ask for a version fetches it.
            </p>
          ) : (
            <Table data-testid="bitswan-cli-list">
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Mirrored</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>SHA-256</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {software.data?.bitswan_cli.map((row) => (
                  <TableRow key={row.version} data-testid={`bitswan-cli-${row.version}`}>
                    <TableCell className="font-medium">{row.version}</TableCell>
                    <TableCell className="text-muted-foreground">{when(row.fetched_at)}</TableCell>
                    <TableCell className="text-muted-foreground">{megabytes(row.size)}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {row.sha256.slice(0, 12)}…
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="space-y-2">
            <p className="text-sm font-medium">How a server downloads it</p>
            <Copyable
              label="the bitswan CLI download"
              text={`curl -fsSL ${api}/api/automation_server/bitswan -o bitswan
chmod +x bitswan
sudo mv bitswan /usr/local/bin/`}
            />
            <p className="text-xs text-muted-foreground">
              Add
              <code className="mx-1 rounded bg-muted px-1">?version=v2026.09.17.112</code>
              to pin a release. Setting a server up normally runs
              <code className="mx-1 rounded bg-muted px-1">{`${api}/api/automation_server/install.sh`}</code>,
              which does this and registers the server in one command.
            </p>
          </div>
        </CardContent>
      </Card>
        <Releases product="bailey" />
        <Demos />
        <RegistryImages group="bailey" software={software} registryHost={registryHost} />
      </section>

      <section className="space-y-4" data-testid="shared-section">
        <div className="border-b pb-2">
          <h3 className="text-xl font-semibold tracking-tight">Misc / Trash</h3>
        </div>
        <RegistryImages group="shared" software={software} registryHost={registryHost} />
      <Card data-testid="registry-disk-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-4 w-4" />
            Registry disk
          </CardTitle>
          <CardDescription>
            Read straight off the registry's volume. Each layer counts once in the total,
            because images share them.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {software.data?.registry.usage ? (
            <div className="space-y-3" data-testid="registry-usage">
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
                <p className="text-3xl font-bold tracking-tight">
                  {gigabytes(software.data.registry.usage.total_bytes)}
                </p>
                <p className="text-sm text-muted-foreground">
                  held by the registry, across{' '}
                  {software.data.registry.usage.blob_count} distinct layers
                </p>
              </div>
              <div>
                <div className="h-2 w-full overflow-hidden rounded bg-muted">
                  <div
                    className={
                      software.data.registry.usage.disk_free_bytes <
                      software.data.registry.usage.disk_total_bytes * 0.1
                        ? 'h-full bg-destructive'
                        : 'h-full bg-primary'
                    }
                    style={{
                      width: `${Math.min(
                        100,
                        (software.data.registry.usage.disk_used_bytes /
                          Math.max(1, software.data.registry.usage.disk_total_bytes)) * 100,
                      ).toFixed(1)}%`,
                    }}
                  />
                </div>
                <p className="mt-1 text-sm" data-testid="registry-free">
                  <span className="font-medium">
                    {gigabytes(software.data.registry.usage.disk_free_bytes)} free
                  </span>{' '}
                  <span className="text-muted-foreground">
                    of {gigabytes(software.data.registry.usage.disk_total_bytes)} on the disk the
                    registry writes to.
                  </span>
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="registry-usage-unavailable">
              This AOC cannot see the registry's disk. An install made before the registry's
              volume was mounted here has nothing to read;{' '}
              <code className="rounded bg-muted px-1">update</code> mounts it.
            </p>
          )}

          <div className="rounded-md border p-3">
            <p className="text-sm font-medium">Remove old builds</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Keeps the newest builds of each image, and whatever{' '}
              <code className="rounded bg-muted px-1">latest</code> points at however old it is.
              Moving tags are never removed.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-sm">Keep</span>
              <Input
                type="number"
                min="1"
                value={keep}
                onChange={(e) => setKeep(e.target.value)}
                className="w-20"
                data-testid="prune-keep"
              />
              <span className="text-sm">builds of each image.</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => prune.mutate(false)}
                disabled={prune.isPending || Number(keep) < 1}
                data-testid="prune-preview"
              >
                {prune.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Show what would go
              </Button>
              {planned && !planned.applied && planned.removed.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => prune.mutate(true)}
                  disabled={prune.isPending}
                  data-testid="prune-apply"
                >
                  Remove {planned.removed.length}
                </Button>
              )}
            </div>
            {planned && (
              <div className="mt-3 space-y-2" data-testid="prune-result">
                {planned.removed.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Nothing to remove at {planned.keep} per image.
                  </p>
                ) : (
                  <>
                    <p className="text-sm">
                      {planned.applied ? 'Removed' : 'Would remove'} {planned.removed.length} builds.
                    </p>
                    <div className="max-h-40 overflow-y-auto rounded bg-muted/50 p-2">
                      {planned.removed.map((r) => (
                        <code key={`${r.repository}:${r.tag}`} className="block text-xs">
                          {r.repository}:{r.tag}
                        </code>
                      ))}
                    </div>
                  </>
                )}
                <p className="text-xs text-muted-foreground">{planned.note}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      </section>

    </div>
  );
}
